import React, { useState, useEffect, useMemo } from 'react';
import { Search, Filter, Upload, ArrowUpDown, ArrowUp, ArrowDown, X, ChevronLeft, ChevronRight } from 'lucide-react';
import Papa from 'papaparse';

const App = () => {
    const [data, setData] = useState([]);
    const [columns, setColumns] = useState([]);
    const [allColumns, setAllColumns] = useState([]);
    const [filters, setFilters] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [hasUploadedData, setHasUploadedData] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [modalImage, setModalImage] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [imageZoom, setImageZoom] = useState(1);
    const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [parseProgress, setParseProgress] = useState(0);
    const [isParsingLarge, setIsParsingLarge] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(20);
    const [totalPages, setTotalPages] = useState(1);

    const sampleCSV = '';
    const isImageColumn = (columnName) => {
        return columnName.toLowerCase().includes('image') ||
            columnName.toLowerCase().includes('img') ||
            columnName.toLowerCase().includes('photo');
    };

    const isLargeImageColumn = (columnName) => {
        const name = columnName.toLowerCase();
        return isImageColumn(columnName) && (
            name.includes('800') ||
            name.includes('500') ||
            name.includes('1000') ||
            name.includes('large') ||
            name.includes('full') ||
            name.includes('big')
        );
    };

    const isTableImageColumn = (columnName) => {
        const name = columnName.toLowerCase();
        return isImageColumn(columnName) && !isLargeImageColumn(columnName);
    };

    const parseCSV = (csvText) => {
        const parsed = Papa.parse(csvText, {
            header: true,
            dynamicTyping: false,
            skipEmptyLines: true,
            delimiter: ','
        });

        const headers = parsed.meta.fields;
        const rows = parsed.data.map(row => {
            const processedRow = {};

            headers.forEach(header => {
                let value = row[header] || '';
                value = value.replace(/^"/, '').replace(/"$/, '').trim();

                if (isImageColumn(header) && value.includes(';')) {
                    const urls = value.split(';')
                        .map(url => url.replace(/^"/, '').replace(/"$/, '').trim())
                        .filter(url => url.length > 0);
                    processedRow[header] = urls;
                } else {
                    processedRow[header] = value;
                }
            });

            return processedRow;
        });

        return { headers, rows };
    };

    const parseLargeCSV = async (csvText) => {
        return new Promise((resolve, reject) => {
            setIsParsingLarge(true);
            setParseProgress(0);

            const workerCode = `
        importScripts('https://cdnjs.cloudflare.com/ajax/libs/PapaParse/5.4.1/papaparse.min.js');
        
        self.onmessage = function(e) {
          const { csvText, chunkSize } = e.data;
          
          try {
            let allRows = [];
            let headers = [];
            let processed = 0;
            
            Papa.parse(csvText, {
              header: true,
              dynamicTyping: false,
              skipEmptyLines: true,
              delimiter: ',',
              chunk: function(results, parser) {
                if (headers.length === 0) {
                  headers = results.meta.fields;
                }
                
                const processedChunk = results.data.map(row => {
                  const processedRow = {};
                  headers.forEach(header => {
                    let value = row[header] || '';
                    value = value.replace(/^"/, '').replace(/"$/, '').trim();
                    
                    const isImageCol = header.toLowerCase().includes('image') || 
                                     header.toLowerCase().includes('img') || 
                                     header.toLowerCase().includes('photo');
                    
                    if (isImageCol && value.includes(';')) {
                      const urls = value.split(';')
                        .map(url => url.replace(/^"/, '').replace(/"$/, '').trim())
                        .filter(url => url.length > 0);
                      processedRow[header] = urls;
                    } else {
                      processedRow[header] = value;
                    }
                  });
                  return processedRow;
                });
                
                allRows = allRows.concat(processedChunk);
                processed += results.data.length;
                
                const totalLines = csvText.split('\\n').length - 1;
                const progress = Math.min(95, (processed / totalLines) * 100);
                self.postMessage({ type: 'progress', progress });
              },
              complete: function() {
                self.postMessage({ 
                  type: 'complete', 
                  data: { headers, rows: allRows } 
                });
              },
              error: function(error) {
                self.postMessage({ 
                  type: 'error', 
                  error: error.message || 'Parsing failed' 
                });
              }
            });
          } catch (error) {
            self.postMessage({ 
              type: 'error', 
              error: error.message || 'Worker failed' 
            });
          }
        };
      `;

            const blob = new Blob([workerCode], { type: 'application/javascript' });
            const worker = new Worker(URL.createObjectURL(blob));

            worker.onmessage = function (e) {
                const { type, progress, data, error } = e.data;

                switch (type) {
                    case 'progress':
                        setParseProgress(progress);
                        break;
                    case 'complete':
                        setParseProgress(100);
                        worker.terminate();
                        URL.revokeObjectURL(blob);
                        setTimeout(() => {
                            setIsParsingLarge(false);
                            resolve(data);
                        }, 100);
                        break;
                    case 'error':
                        worker.terminate();
                        URL.revokeObjectURL(blob);
                        setIsParsingLarge(false);
                        reject(new Error(error));
                        break;
                }
            };

            worker.onerror = function (error) {
                worker.terminate();
                URL.revokeObjectURL(blob);
                setIsParsingLarge(false);
                reject(new Error('Worker error: ' + error.message));
            };

            worker.postMessage({ csvText, chunkSize: 1000 });
        });
    };

    const loadInitialData = () => {
        const storageData = loadDataFromStorage();

        if (storageData) {
            setData(storageData.data);
            setColumns(storageData.columns);
            setAllColumns(storageData.allColumns);
            setHasUploadedData(true);
        } else {
            loadSampleData();
        }
    };

    const loadSampleData = () => {
        if (sampleCSV.trim()) {
            const { headers, rows } = parseCSV(sampleCSV);
            const sortedHeaders = sortColumns(headers);
            setColumns(sortedHeaders);
            setAllColumns(headers);
            setData(rows);
        } else {
            setColumns([]);
            setAllColumns([]);
            setData([]);
        }
        setHasUploadedData(false);
    };

    const CHUNK_SIZE = 500;

    const saveDataToStorage = (csvData, csvColumns, csvAllColumns) => {
        try {
            clearStorageChunks();

            const testData = csvData.slice(0, Math.min(100, csvData.length));
            localStorage.setItem('csvTable_test', JSON.stringify(testData));
            localStorage.removeItem('csvTable_test');

            const metadata = {
                totalRows: csvData.length,
                totalChunks: Math.ceil(csvData.length / CHUNK_SIZE),
                columns: csvColumns,
                allColumns: csvAllColumns,
                timestamp: Date.now(),
                chunkSize: CHUNK_SIZE
            };

            localStorage.setItem('csvTable_meta', JSON.stringify(metadata));

            let savedChunks = 0;
            for (let i = 0; i < csvData.length; i += CHUNK_SIZE) {
                const chunkIndex = Math.floor(i / CHUNK_SIZE);
                const chunk = csvData.slice(i, i + CHUNK_SIZE);

                try {
                    localStorage.setItem(`csvTable_chunk_${chunkIndex}`, JSON.stringify(chunk));
                    savedChunks++;
                } catch (chunkError) {
                    throw new Error(`Failed to save chunk ${chunkIndex}: ${chunkError.message}`);
                }
            }

            metadata.savedChunks = savedChunks;
            localStorage.setItem('csvTable_meta', JSON.stringify(metadata));

            return true;

        } catch (error) {
            clearStorageChunks();

            if (error.name === 'QuotaExceededError' || error.message.includes('QuotaExceededError')) {
                if (CHUNK_SIZE > 100) {
                    return saveDataWithSmallerChunks(csvData, csvColumns, csvAllColumns, 100);
                }
                alert('Storage quota exceeded. The dataset is too large to save locally. Data will not persist after refresh.');
                return false;
            } else {
                console.error('Storage error:', error);
                return false;
            }
        }
    };

    const saveDataWithSmallerChunks = (csvData, csvColumns, csvAllColumns, smallerChunkSize) => {
        try {
            clearStorageChunks();

            const metadata = {
                totalRows: csvData.length,
                totalChunks: Math.ceil(csvData.length / smallerChunkSize),
                columns: csvColumns,
                allColumns: csvAllColumns,
                timestamp: Date.now(),
                chunkSize: smallerChunkSize
            };

            localStorage.setItem('csvTable_meta', JSON.stringify(metadata));

            let savedChunks = 0;
            for (let i = 0; i < csvData.length; i += smallerChunkSize) {
                const chunkIndex = Math.floor(i / smallerChunkSize);
                const chunk = csvData.slice(i, i + smallerChunkSize);
                localStorage.setItem(`csvTable_chunk_${chunkIndex}`, JSON.stringify(chunk));
                savedChunks++;
            }

            metadata.savedChunks = savedChunks;
            localStorage.setItem('csvTable_meta', JSON.stringify(metadata));

            return true;

        } catch (error) {
            clearStorageChunks();
            alert('Storage quota exceeded. The dataset is too large to save locally. Data will not persist after refresh.');
            return false;
        }
    };

    const loadDataFromStorage = () => {
        try {
            const metaData = localStorage.getItem('csvTable_meta');
            if (!metaData) return null;

            const metadata = JSON.parse(metaData);
            const allData = [];

            for (let i = 0; i < metadata.totalChunks; i++) {
                const chunkData = localStorage.getItem(`csvTable_chunk_${i}`);
                if (chunkData) {
                    try {
                        const chunk = JSON.parse(chunkData);
                        allData.push(...chunk);
                    } catch (parseError) {
                        console.warn(`Failed to parse chunk ${i}:`, parseError);
                    }
                } else {
                    console.warn(`Missing chunk ${i} of ${metadata.totalChunks}`);
                }
            }

            if (allData.length !== metadata.totalRows) {
                console.warn(`Data mismatch: expected ${metadata.totalRows} rows, got ${allData.length}`);
            }

            return {
                data: allData,
                columns: metadata.columns,
                allColumns: metadata.allColumns
            };
        } catch (error) {
            console.error('Error loading chunked data:', error);
            clearStorageChunks();
            return null;
        }
    };

    const clearStorageChunks = () => {
        localStorage.removeItem('csvTable_meta');

        const keys = Object.keys(localStorage);
        keys.forEach(key => {
            if (key.startsWith('csvTable_chunk_')) {
                localStorage.removeItem(key);
            }
        });

        localStorage.removeItem('csvTableData');
        localStorage.removeItem('csvTableColumns');
        localStorage.removeItem('csvTableAllColumns');
    };

    const clearStoredData = () => {
        clearStorageChunks();
        loadSampleData();
        setHasUploadedData(false);
        setFilters({});
        setSearchTerm('');
        setCurrentPage(1);
    };

    const sortColumns = (headers) => {
        const tableImageColumns = headers.filter(col => isTableImageColumn(col));
        const largeImageColumns = headers.filter(col => isLargeImageColumn(col));
        const otherColumns = headers.filter(col => !isImageColumn(col));

        const result = [...tableImageColumns, ...otherColumns];
        return result;
    };

    useEffect(() => {
        loadInitialData();
    }, []);

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file && file.type === 'text/csv') {
            setLoading(true);
            const reader = new FileReader();
            reader.onload = async (e) => {
                try {
                    let parseResult;
                    if (file.size > 5 * 1024 * 1024) {
                        parseResult = await parseLargeCSV(e.target.result);
                    } else {
                        parseResult = parseCSV(e.target.result);
                    }

                    const { headers, rows } = parseResult;
                    const sortedHeaders = sortColumns(headers);

                    setColumns(sortedHeaders);
                    setAllColumns(headers);
                    setData(rows);
                    setCurrentPage(1);

                    const saveSuccess = saveDataToStorage(rows, sortedHeaders, headers);

                    setHasUploadedData(true);
                    setFilters({});
                    setSearchTerm('');
                } catch (error) {
                    alert('Error parsing CSV file. Please check the format.');
                } finally {
                    setLoading(false);
                    setIsParsingLarge(false);
                    setParseProgress(0);
                }
            };
            reader.readAsText(file);
        } else {
            alert('Please select a valid CSV file');
        }
    };

    const handleFilterChange = (column, value) => {
        setFilters(prev => ({
            ...prev,
            [column]: value
        }));
        setCurrentPage(1);
    };

    const clearFilters = () => {
        setFilters({});
        setSearchTerm('');
        setSortConfig({ key: null, direction: 'asc' });
        setCurrentPage(1);
    };

    const handleSort = (column) => {
        if (isTableImageColumn(column)) return;

        let direction = 'asc';
        if (sortConfig.key === column && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key: column, direction });
        setCurrentPage(1);
    };

    const sortData = (data) => {
        if (!sortConfig.key) return data;

        return [...data].sort((a, b) => {
            const aValue = a[sortConfig.key] || '';
            const bValue = b[sortConfig.key] || '';

            const aNum = parseFloat(aValue);
            const bNum = parseFloat(bValue);

            let comparison = 0;

            if (!isNaN(aNum) && !isNaN(bNum)) {
                comparison = aNum - bNum;
            } else {
                comparison = aValue.toString().toLowerCase().localeCompare(bValue.toString().toLowerCase());
            }

            return sortConfig.direction === 'asc' ? comparison : -comparison;
        });
    };

    const filteredData = useMemo(() => {
        let filtered = data.filter(row => {
            const matchesSearch = searchTerm === '' ||
                Object.entries(row).some(([key, value]) => {
                    if (isImageColumn(key)) return false;
                    return value.toLowerCase().includes(searchTerm.toLowerCase());
                });

            const matchesFilters = Object.entries(filters).every(([column, filterValue]) => {
                if (!filterValue) return true;
                return row[column]?.toLowerCase().includes(filterValue.toLowerCase());
            });

            return matchesSearch && matchesFilters;
        });

        return sortData(filtered);
    }, [data, searchTerm, filters, sortConfig]);

    useEffect(() => {
        const total = Math.ceil(filteredData.length / itemsPerPage);
        setTotalPages(total);
        if (currentPage > total && total > 0) {
            setCurrentPage(total);
        }
    }, [filteredData.length, itemsPerPage, currentPage]);

    const paginatedData = useMemo(() => {
        if (filteredData.length <= itemsPerPage) {
            return filteredData;
        }

        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filteredData.slice(startIndex, endIndex);
    }, [filteredData, currentPage, itemsPerPage]);

    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);

            const tableContainer = document.querySelector('.overflow-x-auto');
            if (tableContainer) {
                tableContainer.scrollTop = 0;
            }

            const tableSection = document.querySelector('.bg-white.rounded-lg.shadow-sm.overflow-hidden');
            if (tableSection) {
                tableSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    };

    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 5;

        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            let start = Math.max(1, currentPage - 2);
            let end = Math.min(totalPages, start + maxVisible - 1);

            if (end - start + 1 < maxVisible) {
                start = Math.max(1, end - maxVisible + 1);
            }

            for (let i = start; i <= end; i++) {
                pages.push(i);
            }
        }

        return pages;
    };

    const getTableImage = (row) => {
        const tableImageColumns = columns.filter(col => isTableImageColumn(col));

        const sizeOrder = ['200', '150', '100', 'thumb', 'small'];

        for (const size of sizeOrder) {
            const sizedColumn = tableImageColumns.find(col => {
                const hasInName = col.toLowerCase().includes(size);
                const images = row[col];

                if (!images) return false;

                const imageArray = Array.isArray(images) ? images : [images];
                const hasValidImage = imageArray.some(img => img && img.length > 0);

                return (hasInName || imageArray.some(img => img.toLowerCase().includes(size))) && hasValidImage;
            });

            if (sizedColumn) {
                const images = row[sizedColumn];
                const imageArray = Array.isArray(images) ? images : [images];
                const firstValidImage = imageArray.find(img => img && img.length > 0);
                return { column: sizedColumn, src: firstValidImage, allImages: imageArray };
            }
        }

        const firstTableImageColumn = tableImageColumns.find(col => {
            const images = row[col];
            if (!images) return false;
            const imageArray = Array.isArray(images) ? images : [images];
            return imageArray.some(img => img && img.length > 0);
        });

        if (firstTableImageColumn) {
            const images = row[firstTableImageColumn];
            const imageArray = Array.isArray(images) ? images : [images];
            const firstValidImage = imageArray.find(img => img && img.length > 0);
            return { column: firstTableImageColumn, src: firstValidImage, allImages: imageArray };
        }

        return { column: tableImageColumns[0] || 'image', src: null, allImages: [] };
    };

    const getModalImage = (row) => {
        const largeImageColumns = allColumns.filter(col => isLargeImageColumn(col));

        const sizeOrder = ['800', '500', '1000', 'large', 'full', 'big'];

        for (const size of sizeOrder) {
            const sizedColumn = largeImageColumns.find(col => {
                const hasInName = col.toLowerCase().includes(size);
                const images = row[col];

                if (!images) return false;

                const imageArray = Array.isArray(images) ? images : [images];
                const hasValidImage = imageArray.some(img => img && img.length > 0);

                return hasInName && hasValidImage;
            });

            if (sizedColumn) {
                const images = row[sizedColumn];
                const imageArray = Array.isArray(images) ? images : [images];
                const firstValidImage = imageArray.find(img => img && img.length > 0);
                return { column: sizedColumn, src: firstValidImage, allImages: imageArray };
            }
        }

        const firstLargeImageColumn = largeImageColumns.find(col => {
            const images = row[col];
            if (!images) return false;
            const imageArray = Array.isArray(images) ? images : [images];
            return imageArray.some(img => img && img.length > 0);
        });

        if (firstLargeImageColumn) {
            const images = row[firstLargeImageColumn];
            const imageArray = Array.isArray(images) ? images : [images];
            const firstValidImage = imageArray.find(img => img && img.length > 0);
            return { column: firstLargeImageColumn, src: firstValidImage, allImages: imageArray };
        }

        return getTableImage(row);
    };

    const handleImageClick = (rowData, rowIndex) => {
        const modalImageData = getModalImage(rowData);
        const tableImageData = getTableImage(rowData);

        setModalImage({
            src: modalImageData.src || '/placeholder-no-image.png',
            tableSrc: tableImageData.src,
            alt: `Image - ${rowData.approval_code || rowData.product_name || rowData.name || `Row ${rowIndex + 1}`}`,
            rowData: rowData,
            modalColumn: modalImageData.column,
            tableColumn: tableImageData.column,
            allModalImages: modalImageData.allImages || [],
            allTableImages: tableImageData.allImages || []
        });
        setCurrentImageIndex(0);
    };

    const closeModal = () => {
        setModalImage(null);
        setCurrentImageIndex(0);
        setImageZoom(1);
        setImagePosition({ x: 0, y: 0 });
        setIsDragging(false);
    };

    const nextImage = () => {
        if (modalImage && modalImage.allModalImages.length > 1) {
            setCurrentImageIndex((prev) =>
                prev < modalImage.allModalImages.length - 1 ? prev + 1 : 0
            );
            setImageZoom(1);
            setImagePosition({ x: 0, y: 0 });
        }
    };

    const prevImage = () => {
        if (modalImage && modalImage.allModalImages.length > 1) {
            setCurrentImageIndex((prev) =>
                prev > 0 ? prev - 1 : modalImage.allModalImages.length - 1
            );
            setImageZoom(1);
            setImagePosition({ x: 0, y: 0 });
        }
    };

    const handleWheel = (e) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        setImageZoom(prev => Math.max(0.5, Math.min(3, prev + delta)));
    };

    const handleMouseDown = (e) => {
        if (imageZoom > 1) {
            setIsDragging(true);
            setDragStart({
                x: e.clientX - imagePosition.x,
                y: e.clientY - imagePosition.y
            });
        }
    };

    const handleMouseMove = (e) => {
        if (isDragging && imageZoom > 1) {
            setImagePosition({
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y
            });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const resetZoom = () => {
        setImageZoom(1);
        setImagePosition({ x: 0, y: 0 });
    };

    const handleModalClick = (e) => {
        if (e.target === e.currentTarget) {
            closeModal();
        }
    };

    useEffect(() => {
        const handleEscape = (e) => {
            if (modalImage) {
                if (e.key === 'Escape') {
                    closeModal();
                } else if (e.key === 'ArrowRight') {
                    nextImage();
                } else if (e.key === 'ArrowLeft') {
                    prevImage();
                } else if (e.key === 'r' || e.key === 'R') {
                    resetZoom();
                }
            }
        };

        const handleGlobalMouseMove = (e) => handleMouseMove(e);
        const handleGlobalMouseUp = () => handleMouseUp();

        document.addEventListener('keydown', handleEscape);
        if (isDragging) {
            document.addEventListener('mousemove', handleGlobalMouseMove);
            document.addEventListener('mouseup', handleGlobalMouseUp);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.removeEventListener('mousemove', handleGlobalMouseMove);
            document.removeEventListener('mouseup', handleGlobalMouseUp);
        };
    }, [modalImage, currentImageIndex, isDragging, dragStart]);

    return (
        <div className="min-h-screen bg-gray-50 p-4">
            <div className="max-w-7xl mx-auto">
                <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                    <h1 className="text-2xl font-bold text-gray-900 mb-4">CSV Table Viewer</h1>

                    <div className="flex items-center gap-4 mb-4">
                        <label className="flex items-center gap-2 bg-blue-500 text-white px-4 py-2 rounded-lg cursor-pointer hover:bg-blue-600 transition-colors">
                            <Upload className="w-4 h-4" />
                            Upload CSV
                            <input
                                type="file"
                                accept=".csv"
                                onChange={handleFileUpload}
                                className="hidden"
                            />
                        </label>
                        <span className="text-sm text-gray-600">
                            {data.length} rows loaded
                            {hasUploadedData && (
                                <span>
                                    {' (from uploaded file'}
                                    {localStorage.getItem('csvTable_meta') ? ' - saved locally' : ' - not saved locally'}
                                    {')'}
                                </span>
                            )}
                        </span>
                        {hasUploadedData && (
                            <button
                                onClick={clearStoredData}
                                className="text-sm text-red-600 hover:text-red-800 ml-4"
                            >
                                Clear stored data
                            </button>
                        )}
                    </div>

                    <div className="relative mb-4">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                            type="text"
                            placeholder="Search across all columns..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>

                    {(Object.values(filters).some(v => v) || searchTerm || sortConfig.key) && (
                        <button
                            onClick={clearFilters}
                            className="text-sm text-blue-600 hover:text-blue-800 mb-4"
                        >
                            Clear all filters and sorting
                        </button>
                    )}
                </div>

                {loading && (
                    <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
                        {isParsingLarge ? (
                            <div>
                                <p className="text-gray-600 mb-2">Processing large CSV with Web Worker...</p>
                                <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                                    <div
                                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                        style={{ width: `${parseProgress}%` }}
                                    ></div>
                                </div>
                                <p className="text-sm text-gray-500">{Math.round(parseProgress)}% complete</p>
                            </div>
                        ) : (
                            <p className="text-gray-600">Processing CSV data...</p>
                        )}
                    </div>
                )}

                {!loading && data.length > 0 && (
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        {columns.map((column) => (
                                            <th
                                                key={column}
                                                className="px-2 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200"
                                                style={{
                                                    minWidth: isTableImageColumn(column) ? '80px' : '140px',
                                                    maxWidth: isTableImageColumn(column) ? '80px' : '220px',
                                                    width: isTableImageColumn(column) ? '80px' : 'auto'
                                                }}
                                            >
                                                <div className="flex flex-col h-full justify-between" style={{ minHeight: '60px' }}>
                                                    <div className="flex items-center justify-between mb-2 h-5 w-full">
                                                        <div className="flex items-center gap-1 flex-1 min-w-0">
                                                            {isTableImageColumn(column) && <span className="text-blue-500 text-sm flex-shrink-0">🖼️</span>}
                                                            <span
                                                                className="font-semibold text-gray-700 truncate text-xs leading-tight"
                                                                title={column.replace(/_/g, ' ')}
                                                                style={{ maxWidth: isTableImageColumn(column) ? '50px' : '120px' }}
                                                            >
                                                                {column.replace(/_/g, ' ')}
                                                            </span>
                                                        </div>

                                                        {!isTableImageColumn(column) && (
                                                            <button
                                                                onClick={() => handleSort(column)}
                                                                className="flex-shrink-0 ml-1 text-gray-400 hover:text-gray-600 transition-colors"
                                                                title={`Sort by ${column}`}
                                                            >
                                                                {sortConfig.key === column ? (
                                                                    sortConfig.direction === 'asc' ? (
                                                                        <ArrowUp className="w-3 h-3" />
                                                                    ) : (
                                                                        <ArrowDown className="w-3 h-3" />
                                                                    )
                                                                ) : (
                                                                    <ArrowUpDown className="w-3 h-3" />
                                                                )}
                                                            </button>
                                                        )}
                                                    </div>

                                                    <div className="h-7 flex items-center">
                                                        {!isTableImageColumn(column) ? (
                                                            <div className="relative w-full">
                                                                <Filter className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3 z-10" />
                                                                <input
                                                                    type="text"
                                                                    placeholder={`Filter...`}
                                                                    value={filters[column] || ''}
                                                                    onChange={(e) => handleFilterChange(column, e.target.value)}
                                                                    className="w-full pl-7 pr-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent bg-white"
                                                                />
                                                            </div>
                                                        ) : (
                                                            <div className="w-full h-7 flex items-center justify-center">
                                                                <span className="text-xs text-gray-400">No filter</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {paginatedData.map((row, index) => {
                                        const tableImageData = getTableImage(row);

                                        return (
                                            <tr key={index} className="hover:bg-gray-50">
                                                {columns.map((column) => (
                                                    <td
                                                        key={column}
                                                        className="px-2 py-4 whitespace-nowrap"
                                                        style={{
                                                            minWidth: isTableImageColumn(column) ? '80px' : '140px',
                                                            maxWidth: isTableImageColumn(column) ? '80px' : '220px'
                                                        }}
                                                    >
                                                        {isTableImageColumn(column) ? (
                                                            <div className="w-16 h-16 flex-shrink-0">
                                                                <img
                                                                    src={tableImageData.src || '/placeholder-no-image.png'}
                                                                    alt={`${column} for row ${index + 1}`}
                                                                    className="w-full h-full object-cover rounded-lg border border-gray-200 cursor-pointer hover:border-blue-500 transition-colors"
                                                                    onClick={() => handleImageClick(row, index)}
                                                                    onError={(e) => {
                                                                        if (e.target.src !== window.location.origin + '/placeholder-broken.png') {
                                                                            e.target.src = '/placeholder-broken.png';
                                                                        }
                                                                    }}
                                                                    title="Click to enlarge"
                                                                />
                                                            </div>
                                                        ) : (
                                                            <div
                                                                className="text-sm text-gray-900 truncate"
                                                                title={row[column]}
                                                                style={{ maxWidth: '200px' }}
                                                            >
                                                                {row[column] || '-'}
                                                            </div>
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <p className="text-sm text-gray-700">
                                    Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length} results
                                </p>
                                {filteredData.length > itemsPerPage && (
                                    <span className="text-xs text-gray-500">
                                        (Page {currentPage} of {totalPages})
                                    </span>
                                )}
                            </div>

                            {filteredData.length > itemsPerPage && (
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => goToPage(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Previous
                                    </button>

                                    <div className="flex items-center gap-1">
                                        {getPageNumbers().map((page) => (
                                            <button
                                                key={page}
                                                onClick={() => goToPage(page)}
                                                className={`px-3 py-1 text-sm border rounded ${page === currentPage
                                                    ? 'bg-blue-500 text-white border-blue-500'
                                                    : 'border-gray-300 hover:bg-gray-100'
                                                    }`}
                                            >
                                                {page}
                                            </button>
                                        ))}
                                    </div>

                                    <button
                                        onClick={() => goToPage(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {!loading && data.length > 0 && filteredData.length === 0 && (
                    <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                        <div className="text-gray-400 mb-4">
                            <Search className="w-16 h-16 mx-auto mb-4" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
                        <p className="text-gray-600 mb-4">
                            Try adjusting your search terms or filters to find what you're looking for.
                        </p>
                        <button
                            onClick={clearFilters}
                            className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
                        >
                            Clear all filters
                        </button>
                    </div>
                )}

                {!loading && data.length === 0 && (
                    <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                        <div className="text-gray-400 mb-4">
                            <Upload className="w-16 h-16 mx-auto mb-4" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No CSV data loaded</h3>
                        <p className="text-gray-600">Upload a CSV file to get started</p>
                    </div>
                )}
            </div>

            {modalImage && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
                    onClick={handleModalClick}
                >
                    <div
                        className="relative bg-white rounded-lg overflow-hidden shadow-2xl flex flex-col"
                        style={{ width: '800px', height: '500px' }}
                    >
                        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white flex-shrink-0">
                            <div className="flex-1 min-w-0">
                                <h3 className="text-lg font-semibold text-gray-900 truncate">
                                    {modalImage.alt}
                                </h3>
                                <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                                    {modalImage.rowData.approval_code && (
                                        <span>
                                            {modalImage.rowData.status && `${modalImage.rowData.status} • `}
                                            {modalImage.rowData.category && modalImage.rowData.category}
                                        </span>
                                    )}
                                    {modalImage.allModalImages.length > 1 && (
                                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                                            {currentImageIndex + 1} of {modalImage.allModalImages.length}
                                        </span>
                                    )}
                                    {modalImage.modalColumn !== modalImage.tableColumn && (
                                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                                            High Resolution ({modalImage.modalColumn.replace(/_/g, ' ')})
                                        </span>
                                    )}
                                </div>
                            </div>
                            <button
                                onClick={closeModal}
                                className="ml-4 text-gray-400 hover:text-gray-600 transition-colors"
                                title="Close (Esc)"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div
                            className="relative bg-gray-50 flex items-center justify-center flex-grow overflow-hidden"
                            onWheel={handleWheel}
                            style={{ cursor: isDragging ? 'grabbing' : (imageZoom > 1 ? 'grab' : 'default') }}
                        >
                            {modalImage.allModalImages.length > 1 && (
                                <button
                                    onClick={prevImage}
                                    className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition-colors"
                                    title="Previous image (←)"
                                >
                                    <ChevronLeft className="w-6 h-6" />
                                </button>
                            )}

                            <img
                                src={modalImage.allModalImages[currentImageIndex] || modalImage.src}
                                alt={`${modalImage.alt} - Image ${currentImageIndex + 1}`}
                                className="object-contain rounded-lg select-none"
                                style={{
                                    maxWidth: imageZoom === 1 ? '720px' : 'none',
                                    maxHeight: imageZoom === 1 ? '360px' : 'none',
                                    transform: `scale(${imageZoom}) translate(${imagePosition.x / imageZoom}px, ${imagePosition.y / imageZoom}px)`,
                                    cursor: isDragging ? 'grabbing' : (imageZoom > 1 ? 'grab' : 'zoom-in')
                                }}
                                onMouseDown={handleMouseDown}
                                onDoubleClick={() => imageZoom === 1 ? setImageZoom(2) : resetZoom()}
                                onError={(e) => {
                                    if (e.target.src !== window.location.origin + '/placeholder-broken.png') {
                                        e.target.src = '/placeholder-broken.png';
                                    }
                                }}
                                draggable={false}
                            />

                            {modalImage.allModalImages.length > 1 && (
                                <button
                                    onClick={nextImage}
                                    className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition-colors"
                                    title="Next image (→)"
                                >
                                    <ChevronRight className="w-6 h-6" />
                                </button>
                            )}

                            <div className="absolute top-4 right-4 z-10 flex flex-col space-y-2">
                                <button
                                    onClick={() => setImageZoom(prev => Math.min(3, prev + 0.2))}
                                    className="bg-black bg-opacity-50 text-white p-2 rounded hover:bg-opacity-75 transition-colors"
                                    title="Zoom in (scroll up)"
                                >
                                    <span className="text-lg font-bold">+</span>
                                </button>
                                <button
                                    onClick={() => setImageZoom(prev => Math.max(0.5, prev - 0.2))}
                                    className="bg-black bg-opacity-50 text-white p-2 rounded hover:bg-opacity-75 transition-colors"
                                    title="Zoom out (scroll down)"
                                >
                                    <span className="text-lg font-bold">−</span>
                                </button>
                                <button
                                    onClick={resetZoom}
                                    className="bg-black bg-opacity-50 text-white p-1 rounded hover:bg-opacity-75 transition-colors text-xs"
                                    title="Reset zoom (R key)"
                                >
                                    Reset
                                </button>
                                <button
                                    onClick={() => setImageZoom(1)}
                                    className="bg-black bg-opacity-50 text-white p-1 rounded hover:bg-opacity-75 transition-colors text-xs"
                                    title="Fit to view"
                                >
                                    Fit
                                </button>
                            </div>

                            {imageZoom !== 1 && (
                                <div className="absolute bottom-4 left-4 z-10 bg-black bg-opacity-50 text-white px-3 py-1 rounded text-sm">
                                    {Math.round(imageZoom * 100)}%
                                </div>
                            )}

                            <div className="absolute bottom-4 right-4 z-10 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-xs">
                                Scroll to zoom • Double-click to toggle • Drag to pan
                            </div>
                        </div>

                        <div className="bg-white border-t border-gray-200 flex-shrink-0">
                            {modalImage.allModalImages.length > 1 ? (
                                <div className="flex justify-center py-3 space-x-2">
                                    {modalImage.allModalImages.map((_, index) => (
                                        <button
                                            key={index}
                                            onClick={() => setCurrentImageIndex(index)}
                                            className={`w-3 h-3 rounded-full transition-colors ${index === currentImageIndex
                                                ? 'bg-blue-500'
                                                : 'bg-gray-300 hover:bg-gray-400'
                                                }`}
                                            title={`Go to image ${index + 1}`}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className="py-3 flex justify-center">
                                    <span className="text-xs text-gray-500">Single Image</span>
                                </div>
                            )}

                            {modalImage.rowData.approval_code && (
                                <div className="px-4 pb-3 border-t border-gray-100">
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <span className="font-medium text-gray-900">Approval Code:</span>
                                            <span className="ml-2 text-gray-600">{modalImage.rowData.approval_code}</span>
                                        </div>
                                        <div>
                                            <span className="font-medium text-gray-900">Status:</span>
                                            <span className="ml-2 text-gray-600">{modalImage.rowData.status}</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default App;